import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL, NAV_LINKS, scrollToId } from "../lib/site";
import { Leaf } from "./Decor";

export default function Navbar({ onEnquire }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 120 : 0);
  };

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-cream/90 backdrop-blur-md border-b border-sand shadow-[0_4px_30px_rgba(17,38,29,0.08)]" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
          <button
            data-testid="nav-brand"
            onClick={() => go("home")}
            className="flex items-center gap-3 group"
            aria-label="Unique Organic Farm — home"
          >
            <img
              src={LOGO_URL}
              alt="Unique Organic Farm logo"
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover ring-1 ring-gold/40 shadow-md transition-transform duration-500 group-hover:rotate-6"
            />
            <span className="text-left leading-tight">
              <span className={`block font-serif font-semibold text-base sm:text-lg tracking-wide transition-colors duration-500 ${scrolled ? "text-forest" : "text-cream"}`}>
                Unique Organic Farm
              </span>
              <span className={`hidden sm:block text-[10px] uppercase tracking-[0.3em] transition-colors duration-500 ${scrolled ? "text-gold" : "text-goldlight"}`}>
                Pure · Fresh · Natural
              </span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-link-${link.id}`}
                onClick={() => go(link.id)}
                className={`relative text-sm font-medium tracking-wide transition-colors duration-500 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-full after:origin-left after:scale-x-0 after:transition-transform after:duration-500 hover:after:scale-x-100 ${
                  scrolled ? "text-forest/80 hover:text-forest after:bg-gold" : "text-cream/85 hover:text-cream after:bg-goldlight"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="enquire-now-btn"
              onClick={() => onEnquire()}
              className={`hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold tracking-[0.18em] uppercase transition-all duration-500 hover:shadow-lg hover:-translate-y-0.5 ${
                scrolled
                  ? "bg-gold text-nightgreen hover:bg-goldlight"
                  : "border border-goldlight/70 text-goldlight hover:bg-goldlight hover:text-nightgreen"
              }`}
            >
              <Leaf className="w-3.5 h-3.5" />
              Enquire Now
            </button>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className={`lg:hidden p-2 rounded-full transition-colors ${scrolled ? "text-forest" : "text-cream"}`}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] bg-nightgreen flex flex-col"
          >
            <div className="flex items-center justify-between px-5 h-16">
              <img src={LOGO_URL} alt="Unique Organic Farm logo" className="w-10 h-10 rounded-full ring-1 ring-gold/40" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu" className="p-2 text-cream">
                <X className="w-7 h-7" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.id}
                  data-testid={`mobile-nav-link-${link.id}`}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(link.id)}
                  className="text-left font-serif text-4xl text-cream/90 py-2.5 border-b border-cream/10 hover:text-goldlight transition-colors"
                >
                  {link.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-enquire-btn"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.65, duration: 0.5 }}
                onClick={() => {
                  setOpen(false);
                  setTimeout(() => onEnquire(), 150);
                }}
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-nightgreen px-8 py-4 text-sm font-semibold tracking-[0.2em] uppercase"
              >
                <Leaf className="w-4 h-4" />
                Enquire Now
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
